/* global BrazeKey, BrazeLogging */
import React from 'react';
import * as braze from '@braze/web-sdk';
import { Braze } from '@foxcorp/cpe-commerce-web';
import BrazeContent from 'components/BrazeContent';
import { isStaticRender } from 'helpers/ContentHelper';
import { getPathRoot } from 'helpers/RouteHelper';
import { getCurrentAuthData, isAnonymousAccount } from 'helpers/Account';

export const initBraze = () => {
  if (isStaticRender()) return;
  const { authData } = getCurrentAuthData();
  braze.initialize(BrazeKey, {
    baseUrl: 'sdk.iad-05.braze.com',
    manageServiceWorkerExternally: true,
    serviceWorkerLocation: '/push-worker.js',
    allowUserSuppliedJavascript: true,
    sessionTimeoutInSeconds: 60,
  });

  // logging based on environment
  if (BrazeLogging) {
    braze.toggleLogging();
  }

  const windowAnonId =
    window?.analytics?.user &&
    window?.analytics?.user()?.anonymousId &&
    window?.analytics?.user()?.anonymousId();
  // set user for braze session
  const brazeUser = authData?.profileId || windowAnonId || null;
  const isAnonymous = isAnonymousAccount(authData);
  if (brazeUser && !isAnonymous) {
    braze.changeUser(brazeUser);
    braze.requestImmediateDataFlush();
  }
  // init braze content cards (banners)
  braze.subscribeToContentCardsUpdates();
  braze.requestContentCardsRefresh();
  // in app messages
  if (isAnonymous) {
    braze.automaticallyShowInAppMessages();
    braze.getUser().setCustomUserAttribute('u_log_stat_web', false);
  } else {
    SoftPushPrimer();
    BrazeCustomEvent('userAuthenticated');
    braze.getUser().setCustomUserAttribute('u_log_stat_web', true);
  }
  // init braze session
  braze.openSession();
  initBrazeActions();
};

export const BrazeFetchContent = () => {
  try {
    braze.requestImmediateDataFlush();
    braze.requestContentCardsRefresh();
    const content = braze.getCachedContentCards();
    return content;
  } catch (error) {
    return null;
  }
};

const initBrazeActions = () => {
  const { api, apiKey } = window.ExternalConfig?.evergent || {};
  Braze.createActions({
    apiKey,
    apiUrl: api,
  });
};

export const BrazeContentRender = (zone, content) => {
  if (isStaticRender()) return null;
  // By default braze still renders an empty content box for empty 'zones'
  // This function is a helper function to assist in being able to
  // place multiple zones on page without said empty content box
  if (!zone) return null;
  if (!content) return null;

  const displayCard = content.cards.find(card => card.extras.zone === zone);

  if (displayCard && displayCard.extras.zone === zone) {
    const type = {
      Banner: 'banner',
      CaptionedImage: 'captionedImage',
    };
    const contentCards = content?.cards?.filter(card => card.extras.zone === zone);
    const sortedCards = contentCards?.sort((a, b) => Date.parse(a.updated) - Date.parse(b.updated));
    const latestCard = sortedCards.pop();
    const typeString = type[latestCard?.extras?.campaign_type];
    const { title, description, imageUrl, linkText, url } = latestCard;

    return (
      <BrazeContent
        braze={braze}
        description={description}
        id={`Braze-Content-${zone}`}
        imageUrl={imageUrl}
        latestCard={latestCard}
        link={url}
        linkText={linkText}
        title={title}
        type={typeString}
      />
    );
  }
  return null;
};

const SoftPushPrimer = () => {
  braze.subscribeToInAppMessage(inAppMessage => {
    if (inAppMessage) {
      // access the key-value pairs, defined as `extras`
      const keyValuePairs = inAppMessage.extras || {};
      console.log('keyValuePairs', keyValuePairs);
      // check the value of our key `msg-id` defined in the Braze dashboard
      if (keyValuePairs['iam-id'] === 'push-primer') {
        if (
          braze.isPushSupported() === false ||
          braze.isPushPermissionGranted() ||
          braze.isPushBlocked()
        ) {
          console.log('push messages not supported');
          return;
        }
        braze.showInAppMessage(inAppMessage);
      } else {
        braze.automaticallyShowInAppMessages();
      }
    }
  });
};

const BrazeCustomEvent = eventName => {
  if (isStaticRender()) return;
  braze.logCustomEvent(eventName);
};

export const LogBrazePathChange = path => {
  if (isStaticRender()) return;
  const pathRoot = getPathRoot(path);
  braze.logCustomEvent('Page_Load', {
    path,
    pathRoot,
  });
};

export * as braze from '@braze/web-sdk';
